import {
  CharacterType,
  ICharacter,
  NotificationType,
  useAppSelector,
  useCharacter,
  useLorebook,
  useTranslation,
} from "@next-platform/client-common-core";
import getCharacterAttachBlockedStatus from "@next-platform/client-common-core/utils/getCharacterAttachBlockedStatus";
import {
  Button,
  ButtonVariant,
  Icon,
  IconSize,
  IconVariants,
  ListEmpty,
  LorebookBadge,
  Modal,
  ModalSize,
  Spinner,
  Text,
  TextVariants,
} from "@next-platform/client-common-spicy-ui";
import { useCallback, useEffect, useMemo, useState } from "react";

import BotListToolbarV2, { getVisibilityFilterOptions } from "@/components/BotListToolbarV2";
import Trans from "@/components/Trans/Trans";
import { FilterKeys, FilterOptions, IOptions, SortOptions } from "@/components/type";
import { FEATURE_FLAGS } from "@/constants";
import { useFeatureFlag, useMyChatbotsData } from "@/hooks";
import useToast from "@/hooks/useToast";
import { convertToSnakeCase } from "@/utils";
import { getAvatarUrl } from "@/utils/getAvatarUrl";

import { LorebookTargetCardProps, SelectableLorebookTarget } from "./types";
import getCharacterAttachedLorebookId from "./utils/getCharacterAttachedLorebookId";
import { sortTargetsByUpdatedAtAsc, sortTargetsByUpdatedAtDesc } from "./utils/sortTargetsByUpdatedAt";

interface IModalChatbotProps {
  isOpen?: boolean;
  onClose: () => void;
  lorebookId: string;
}

const getTargetDescription = (character: ICharacter) => character.greeting || character.title || character.scenario || "";

type GroupBadgeProps = {
  groupBadgeLabel: string;
  membersCount: number;
};

function GroupBadge({ groupBadgeLabel, membersCount }: GroupBadgeProps) {
  return (
    <span
      role="img"
      aria-label={groupBadgeLabel}
      title={groupBadgeLabel}
      data-testid="ModalChatbot-GroupBadge"
      className="inline-flex h-[15px] w-fit flex-none items-center gap-0.5 rounded bg-blue-9 px-1 text-white"
    >
      <Icon icon={IconVariants.USERS} size={IconSize.XSMALL} />
      {membersCount > 0 ? <span className="text-[10px] font-semibold leading-none">{membersCount}</span> : null}
    </span>
  );
}
function LorebookTargetCard({ groupBadgeLabel, isSelected, target, lorebookBadgeLabel, onClick, disabled }: LorebookTargetCardProps) {
  const { character, type } = target;
  const avatarUrl = getAvatarUrl(character.avatar_url ?? "", { returnWithOriginalRatio: true }) as string;
  const shouldShowLorebookBadge = character.attachedLorebookId != null || character.has_lorebooks === true || (character.lorebooks?.length ?? 0) > 0;
  const description = getTargetDescription(character);
  const membersCount = character.sub_characters_count ?? 0;
  const isGroup = type === "group";

  return (
    <button
      type="button"
      data-testid="ModalChatbot-TargetCard"
      onClick={onClick}
      className={`flex w-full items-start gap-sm rounded-md p-sm text-left transition-colors ${
        isSelected ? "bg-gray-5" : "bg-transparent hover:bg-gray-4"
      }`}
      disabled={disabled}
    >
      {avatarUrl ? (
        <img src={avatarUrl} alt={character.name} className="h-[64px] w-[64px] flex-none rounded-md object-cover" />
      ) : (
        <div className="flex h-[64px] w-[64px] flex-none items-center justify-center rounded-md bg-gray-6 text-gray-10">
          <Icon icon={isGroup ? IconVariants.USERS : IconVariants.BOT} size={IconSize.MEDIUM} />
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex min-w-0 items-center gap-1">
          <Text variant={TextVariants.LABEL_MEDIUM_BOLD} className="min-w-0 truncate text-gray-12">
            {character.name}
          </Text>
        </div>
        <div className="flex min-w-0 items-center gap-2 text-gray-10">
          <LorebookBadge lorebookAttached={shouldShowLorebookBadge} lorebookLabel={lorebookBadgeLabel} />
          {isGroup ? <GroupBadge groupBadgeLabel={groupBadgeLabel} membersCount={membersCount} /> : null}
          {character.rating_score ? (
            <span className="flex items-center gap-0.5 text-[11px] leading-4">
              <Icon icon={IconVariants.LIKE} size={IconSize.XSMALL} />
              {character.rating_score}%
            </span>
          ) : null}
          {character.num_messages ? (
            <span className="flex items-center gap-0.5 text-[11px] leading-4">
              <Icon icon={IconVariants.MESSAGE} size={IconSize.XSMALL} />
              {character.num_messages}
            </span>
          ) : null}
          {character.token_count ? (
            <span className="flex items-center gap-0.5 text-[11px] leading-4">
              <Icon icon={IconVariants.BLOCKS} size={IconSize.XSMALL} />
              {character.token_count}
            </span>
          ) : null}
        </div>
        {description ? (
          <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="line-clamp-2 text-gray-10">
            {description}
          </Text>
        ) : null}
      </div>
      <input
        type="radio"
        name="lorebook-target"
        checked={isSelected}
        readOnly
        aria-label={character.name}
        className="mt-1 h-4 w-4 flex-none accent-blue-10"
        disabled={disabled}
      />
    </button>
  );
}

export default function ModalChatbot({ lorebookId, isOpen, onClose }: IModalChatbotProps) {
  const { t } = useTranslation();
  const { displayToast } = useToast();
  const [stateSearch, setStateSearch] = useState("");
  const { isLoading: isCharacterLoading } = useCharacter();
  const [stateIsLorebookLoading, setStateIsLorebookLoading] = useState(false);
  const { onUserAttachLorebook, onUserDetachLorebook } = useLorebook();
  const [stateSelectedTarget, setStateSelectedTarget] = useState<SelectableLorebookTarget | null>(null);
  const [stateDetachedCharacterIds, setStateDetachedCharacterIds] = useState<Set<ICharacter["id"]>>(new Set());
  const sortBy = useAppSelector((state: { settings: { myChatbotSortBy: string } }) => state.settings.myChatbotSortBy);
  // This modal only offers Latest / Oldest. Seed the sort dropdown from the
  // persisted Redux sort so it matches the order the list is actually rendered
  // in; fall back to Latest for any value this modal doesn't expose (e.g. Top Rated).
  const initialSortBy = sortBy === SortOptions.OLDEST ? SortOptions.OLDEST : SortOptions.LATEST;
  const [stateFilterBy, setStateFilterBy] = useState<FilterOptions>(FilterOptions.ALL);
  const [stateSortBy, setStateSortBy] = useState<SortOptions>(initialSortBy);

  // Groups (META) are only offered as attach targets while this flag is on. When it is off we
  // fetch and show standard chatbots only, so the group request is never made.
  const { hasFeatureFlag: isGroupLorebookAttachmentEnabled } = useFeatureFlag(FEATURE_FLAGS.RELEASE_GROUP_LOREBOOK_ATTACHMENT_WEB);

  const chatbotData = useMyChatbotsData({
    initialSortType: convertToSnakeCase(sortBy),
    type: CharacterType.STANDARD,
    includeAttachedLorebookId: true,
  });
  const groupData = useMyChatbotsData({
    initialSortType: convertToSnakeCase(sortBy),
    type: CharacterType.META,
    includeAttachedLorebookId: true,
    isFetchEnabled: isGroupLorebookAttachmentEnabled,
  });

  const filterOptions: IOptions[] = getVisibilityFilterOptions(t);
  const sortOptions: IOptions[] = [
    { label: t("myCreation:sort.latest", "Latest"), value: SortOptions.LATEST },
    { label: t("myCreation:sort.oldest", "Oldest"), value: SortOptions.OLDEST },
  ];

  const selectedCharacterId = stateSelectedTarget?.character.id ?? null;
  const selectedAttachedLorebookId = stateSelectedTarget
    ? getCharacterAttachedLorebookId(stateSelectedTarget.character, stateDetachedCharacterIds)
    : undefined;
  const isThisLorebookAttachedToSelected = selectedAttachedLorebookId === lorebookId;
  const hasDifferentLorebookAttached = selectedAttachedLorebookId != null && !isThisLorebookAttachedToSelected;

  const selectedCharacterBlockedStatus = useMemo(
    () => getCharacterAttachBlockedStatus(stateSelectedTarget?.character),
    [stateSelectedTarget?.character],
  );
  const isSelectedUnderReview = selectedCharacterBlockedStatus === "underReview";
  const isSelectedActionRequired = selectedCharacterBlockedStatus === "actionRequired";
  const isSelectedBlocked = selectedCharacterBlockedStatus !== "none";

  const combinedTargets = useMemo(() => {
    const chatbotTargets = (chatbotData.filteredAndSortedChatbots ?? []).map((character) => ({ character, type: "chatbot" as const }));
    const groupTargets = isGroupLorebookAttachmentEnabled
      ? (groupData.filteredAndSortedChatbots ?? []).map((character) => ({ character, type: "group" as const }))
      : [];
    const targets = [...chatbotTargets, ...groupTargets];
    return stateSortBy === SortOptions.OLDEST ? targets.sort(sortTargetsByUpdatedAtAsc) : targets.sort(sortTargetsByUpdatedAtDesc);
  }, [chatbotData.filteredAndSortedChatbots, groupData.filteredAndSortedChatbots, stateSortBy, isGroupLorebookAttachmentEnabled]);

  const handleFilterChange = useCallback(
    (filterKey: FilterKeys, filterValue?: FilterOptions) => {
      const resolvedValue = filterValue ?? FilterOptions.ALL;
      setStateFilterBy(resolvedValue);
      chatbotData.handleLocalFilter(filterKey, resolvedValue);
      if (isGroupLorebookAttachmentEnabled) groupData.handleLocalFilter(filterKey, resolvedValue);
    },
    [chatbotData.handleLocalFilter, groupData.handleLocalFilter, isGroupLorebookAttachmentEnabled],
  );

  const handleSortChange = useCallback(
    (value: SortOptions) => {
      setStateSortBy(value);
      chatbotData.handleLocalSort(convertToSnakeCase(value));
      if (isGroupLorebookAttachmentEnabled) groupData.handleLocalSort(convertToSnakeCase(value));
    },
    [chatbotData.handleLocalSort, groupData.handleLocalSort, isGroupLorebookAttachmentEnabled],
  );

  const handleClose = useCallback(() => {
    setStateSelectedTarget(null);
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(() => {
    const submit = async () => {
      if (!stateSelectedTarget || !selectedCharacterId || isSelectedBlocked || isThisLorebookAttachedToSelected) return;
      setStateIsLorebookLoading(true);

      if (hasDifferentLorebookAttached && selectedAttachedLorebookId != null) {
        const detachResult = await onUserDetachLorebook(selectedAttachedLorebookId, selectedCharacterId);
        if (detachResult?.error != null) {
          setStateIsLorebookLoading(false);
          displayToast({ message: detachResult.error, type: NotificationType.error });
          return;
        }
        setStateDetachedCharacterIds((previous) => new Set(previous).add(selectedCharacterId));
      }

      const attachResult = await onUserAttachLorebook(lorebookId, { characterId: selectedCharacterId });
      setStateIsLorebookLoading(false);
      if (attachResult?.error != null) {
        displayToast({ message: attachResult.error, type: NotificationType.error });
        return;
      }
      displayToast({
        message: t("lorebook:toast.attachedTo", { defaultValue: "Lorebook attached to {{name}}", name: stateSelectedTarget.character.name }),
        type: NotificationType.success,
      });
      onClose();
    };

    void submit();
  }, [
    displayToast,
    hasDifferentLorebookAttached,
    isSelectedBlocked,
    isThisLorebookAttachedToSelected,
    lorebookId,
    onClose,
    onUserAttachLorebook,
    onUserDetachLorebook,
    selectedAttachedLorebookId,
    selectedCharacterId,
    stateSelectedTarget,
    t,
  ]);

  const handleClickTarget = useCallback((target: SelectableLorebookTarget) => {
    setStateSelectedTarget(target);
  }, []);

  useEffect(() => {
    chatbotData.handleLocalSearch(stateSearch);
    if (isGroupLorebookAttachmentEnabled) groupData.handleLocalSearch(stateSearch);
  }, [chatbotData.handleLocalSearch, groupData.handleLocalSearch, stateSearch, isGroupLorebookAttachmentEnabled]);

  useEffect(() => {
    if (!isOpen) return;
    setStateSelectedTarget(null);
    setStateDetachedCharacterIds(new Set());
    void chatbotData.refetch();
    if (isGroupLorebookAttachmentEnabled) void groupData.refetch();
  }, [chatbotData.refetch, groupData.refetch, isOpen, isGroupLorebookAttachmentEnabled]);

  const resultsCount = combinedTargets.length;
  const isLoading =
    isCharacterLoading ||
    chatbotData.isLoading ||
    chatbotData.isFiltering ||
    (isGroupLorebookAttachmentEnabled && (groupData.isLoading || groupData.isFiltering));
  const hasEmptyStateSearchQuery = stateSearch.trim().length > 0;
  const attachButtonLabel = hasDifferentLorebookAttached
    ? t("lorebook:modal.action.replace", "Replace Lorebook")
    : t("lorebook:modal.action.attach", "Attach Lorebook");
  const isSubmitDisabled = isCharacterLoading || !selectedCharacterId || isSelectedBlocked || isThisLorebookAttachedToSelected;
  const emptyStateDescription = hasEmptyStateSearchQuery
    ? t("lorebook:modal.empty.description", "No chatbots or groups match your search.")
    : t("myCreation:emptyState.default.description", "Start creating chatbots or groups to bring your world to life.");
  const resultsLabel = (
    <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-gray-10">
      {resultsCount > 0 ? t("lorebook:modal.resultsFound", { defaultValue: "{{count}} results found", count: resultsCount }) : ""}
    </Text>
  );

  return (
    <Modal
      title={t("lorebook:modal.attachCharacter.title", "Attach Lorebook")}
      open={isOpen}
      onOpenChange={onClose}
      size={ModalSize.MEDIUM}
      backdrop="blur"
      closeOnActions={false}
      actions={
        <div className="flex justify-end gap-sm">
          <Button variant={ButtonVariant.OUTLINED} type="button" onClick={handleClose} disabled={isCharacterLoading}>
            <Trans i18nKey="lorebook:modal.action.cancel" defaults="Cancel" />
          </Button>
          <Button variant={ButtonVariant.CONTAINED} type="button" loading={stateIsLorebookLoading} disabled={isSubmitDisabled} onClick={handleSubmit}>
            {attachButtonLabel}
          </Button>
        </div>
      }
      data-testid="ModalChatbot"
      description={
        <div className="flex flex-col gap-md pb-md">
          <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-gray-10">
            <Trans
              i18nKey="lorebook:modal.attachCharacter.description"
              defaults="Apply this lorebook to one of your chatbots or groups. Only one lorebook can be active at a time."
            />
          </Text>
          {isThisLorebookAttachedToSelected && !isSelectedBlocked ? (
            <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-yellow-10">
              <Trans
                i18nKey="lorebook:modal.attachCharacter.alreadyAttached"
                defaults="This Lorebook is already attached to the selected chatbot/group."
              />
            </Text>
          ) : null}
          {isSelectedUnderReview ? (
            <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-red-10">
              <Trans
                i18nKey="lorebook:modal.attachCharacter.pendingReview"
                defaults="This chatbot is pending review and can't be attached. Please choose another."
              />
            </Text>
          ) : null}
          {isSelectedActionRequired ? (
            <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-red-10">
              <Trans
                i18nKey="lorebook:modal.attachCharacter.actionRequired"
                defaults="This chatbot needs changes and can't be attached. Please choose another."
              />
            </Text>
          ) : null}
          <BotListToolbarV2
            searchQuery={stateSearch}
            onSearchChange={setStateSearch}
            filterBy={stateFilterBy}
            onFilterChange={handleFilterChange}
            filterOptions={filterOptions}
            sortBy={stateSortBy}
            onSortChange={handleSortChange}
            sortOptions={sortOptions}
            resultsLabel={resultsLabel}
          />
        </div>
      }
    >
      {isLoading ? (
        <div className="flex min-h-[160px] items-center justify-center">
          <Spinner className="mx-auto" />
        </div>
      ) : null}
      {!isLoading && !combinedTargets.length ? (
        <div className="flex min-h-[160px] items-center justify-center">
          <ListEmpty isLoading={false} title={t("lorebook:modal.empty.title", "No Results Found")} description={emptyStateDescription} />
        </div>
      ) : null}
      {!isLoading && combinedTargets.length ? (
        <div className="flex flex-col gap-xs">
          {combinedTargets.map((target) => (
            <LorebookTargetCard
              key={`${target.type}-${target.character.id}`}
              target={target}
              isSelected={selectedCharacterId === target.character.id}
              lorebookBadgeLabel={t("lorebook:modal.badge.lorebook", "Lorebook")}
              groupBadgeLabel={t("lorebook:modal.badge.group", "Group")}
              onClick={() => handleClickTarget(target)}
              disabled={stateIsLorebookLoading}
            />
          ))}
        </div>
      ) : null}
    </Modal>
  );
}
